import { QueryFunction, QueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { Contract } from 'ethers';
import { InterestRateModel, InterestRateModel__factory, SiloRepository } from '@silo/typechain';
import { useWeb3Context, Web3Context } from './web3Context';
import { parseAllQueryData } from '@silo/query';
import { ChecksumAddress, ClientProvider } from '@silo/types';

type InterestRateModelContractKey = [
  key: 'interestRateModelContract',
  web3Context: Web3Context,
  siloAddress: ChecksumAddress,
  assetAddress: ChecksumAddress
];

export const fetchInterestRateModelContract = async ({
  siloRepository,
  siloAddress,
  assetAddress,
  clientProvider,
}: {
  siloRepository: SiloRepository;
  siloAddress: ChecksumAddress;
  assetAddress: ChecksumAddress;
  clientProvider: ClientProvider;
}) => {
  const { interestRateModel } = await siloRepository.assetConfigs(siloAddress, assetAddress);

  return <InterestRateModel>new Contract(interestRateModel, InterestRateModel__factory.abi, clientProvider);
};

const queryInterestRateModelContract: QueryFunction<InterestRateModel, InterestRateModelContractKey> = async ({
  queryKey: [, { contracts, clientProvider }, siloAddress, assetAddress],
}) => {
  return fetchInterestRateModelContract({
    siloRepository: contracts.siloRepository,
    siloAddress,
    assetAddress,
    clientProvider,
  });
};

const useInterestRateModelContracts = (addresses: { silo: ChecksumAddress; asset: ChecksumAddress }[]) => {
  const web3Context = useWeb3Context();

  const queries = useQueries<
    QueryOptions<InterestRateModel, unknown, InterestRateModel, InterestRateModelContractKey>[]
  >({
    queries: addresses.map(({ silo, asset }) => ({
      queryKey: ['interestRateModelContract', web3Context!, silo, asset],
      queryFn: queryInterestRateModelContract,
      enabled: !!web3Context,
    })),
  });

  return {
    queries,
    allData: parseAllQueryData(queries),
  };
};

export const useInterestRateModelContract = (siloAddress: ChecksumAddress, assetAddress: ChecksumAddress) => {
  const {
    queries: [query],
  } = useInterestRateModelContracts([{ silo: siloAddress, asset: assetAddress }]);

  return query.data ?? null;
};

export const useQueryInterestRateModelConfig = (silo: ChecksumAddress, asset: ChecksumAddress) => {
  const interestRateModelContract = useInterestRateModelContract(silo, asset);

  return useQuery(
    ['interestRateModelConfig', interestRateModelContract, silo, asset],
    () => queryConfig(interestRateModelContract!, silo, asset),
    {
      enabled: !!interestRateModelContract,
    }
  );
};

const queryConfig = (interestRateModel: InterestRateModel, silo: ChecksumAddress, asset: ChecksumAddress) => {
  return interestRateModel.getConfig(silo, asset);
};
